import React, {useState, useEffect, useCallback} from 'react'
import {Modal, Button, Form} from 'react-bootstrap'
import './CancelSubscriptionModal.scss'
import {useGetCancelReasonsQuery} from '../../../../services/cellcast.v2'
import {ProductPlan} from '../../../../services/cellcast.v2.types'
import {useI18n} from '../../../Utils/_helpers/useI18n'

interface CancelFeedbackData {
  selectedReasonIds: string[]
  customReason: string
}

interface CancelSubscriptionModalProps {
  show: boolean
  onHide: () => void
  onConfirm: (feedbackData: CancelFeedbackData) => Promise<boolean>
  isCancelling: boolean
  cancelError?: string
  currentPlan?: ProductPlan
  nextBillingDate?: string
  onAfterSuccessClose?: () => void
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  show,
  onHide,
  onConfirm,
  isCancelling,
  cancelError,
  currentPlan,
  nextBillingDate,
  onAfterSuccessClose,
}) => {
  const {t} = useI18n()
  const [currentStep, setCurrentStep] = useState(1)
  const [feedbackData, setFeedbackData] = useState({
    reasons: {} as {[key: string]: boolean},
    otherReason: '',
  })

  const {data: cancelReasonsResponse, isLoading: isLoadingReasons} = useGetCancelReasonsQuery(null)

  const handleClose = useCallback(() => {
    if (currentStep === 3) {
      // After success step, trigger immediate refetch in parent
      onAfterSuccessClose && onAfterSuccessClose()
    }
    setCurrentStep(1)
    setFeedbackData({reasons: {}, otherReason: ''})
    onHide()
  }, [onHide, currentStep, onAfterSuccessClose])

  const handleReasonToggle = (reason: string) => {
    setFeedbackData((prev) => ({
      ...prev,
      reasons: {...prev.reasons, [reason]: !prev.reasons[reason]},
    }))
  }

  const handleDowngrade = () => {
    console.log('Downgrade to lower plan')
    handleClose()
  }

  const handleFinalCancel = useCallback(async () => {
    // Convert selected reasons object to array of selected reason IDs
    const selectedReasonIds = Object.entries(feedbackData.reasons)
      .filter(([_, isSelected]) => isSelected)
      .map(([reasonId]) => reasonId)

    try {
      // Pass feedback data to parent component and wait for result
      const success = await onConfirm({
        selectedReasonIds,
        customReason: feedbackData.otherReason.trim(),
      })

      // Only advance to success step if the API call succeeded
      if (success) {
        setCurrentStep(3)
      }
      // If not successful, we stay on the current step and the error will be displayed via cancelError prop
    } catch (error) {
      // If there's an exception, we also stay on the current step
      console.error('Error cancelling subscription:', error)
    }
  }, [feedbackData, onConfirm])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!show) return
      if (e.key === 'Escape') {
        setCurrentStep(1)
        setFeedbackData({reasons: {}, otherReason: ''})
        onHide()
      }
      if (e.key === 'Enter') {
        if (currentStep === 1) setCurrentStep(2)
        else if (currentStep === 2 && !isCancelling) {
          void handleFinalCancel()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [show, currentStep, onHide, handleFinalCancel, isCancelling])

  const renderStep1 = () => (
    <>
      <Modal.Header closeButton className='border-bottom'>
        <h2 className='fs-5 fw-bold text-dark'>{t('CANCEL_SUBSCRIPTION.TITLE')}</h2>
      </Modal.Header>
      <Modal.Body className='px-4 py-4'>
        <div className='mb-4'>
          <div className='d-flex align-items-start mb-4'>
            <div className='d-flex align-items-center justify-content-center rounded-circle me-3 flex-shrink-0 csm-warning-icon'>
              <span className='fw-bold csm-icon-exclamation'>!</span>
            </div>
            <div className='flex-grow-1'>
              <p className='text-dark mb-1 csm-text-md csm-text-semibold csm-text-line-height'>
                {t('CANCEL_SUBSCRIPTION.YOURE_ON')}{' '}
                <span className='csm-text-bold'>
                  {currentPlan?.name || t('CANCEL_SUBSCRIPTION.PRO_BCARD')} — $
                  {currentPlan?.price || 10}
                  {t('CANCEL_SUBSCRIPTION.MONTH')}
                </span>
                .
              </p>
              <p className='mb-0 csm-text-md csm-text-regular csm-text-line-height'>
                {t('CANCEL_SUBSCRIPTION.LOSE_ACCESS')}{' '}
                <span className='fw-semibold'>{t('CANCEL_SUBSCRIPTION.AI_BCARDS')}</span>{' '}
                {t('CANCEL_SUBSCRIPTION.BILLING_CYCLE_ENDS')}{' '}
                <span className='fw-semibold text-dark csm-text-bold'>
                  {nextBillingDate || 'Oct 09, 2025'}
                </span>
                .
              </p>
              <p className='text-muted mt-3 mb-2 csm-text-sm csm-text-semibold'>
                {t('CANCEL_SUBSCRIPTION.DATA_SECURE')}
              </p>
            </div>
          </div>

          <div className='rounded p-4 mb-4 csm-plan-card'>
            <div className='d-flex align-items-center justify-content-between'>
              <div>
                <div className='text-dark csm-text-md csm-text-bold'>
                  {t('CANCEL_SUBSCRIPTION.CURRENT_PLAN')}
                </div>
                <div className='mt-1 csm-text-md csm-text-regular'>
                  {currentPlan?.name || t('CANCEL_SUBSCRIPTION.PRO_BCARD')} •{' '}
                  {currentPlan?.quotaLimit || 8} {t('CANCEL_SUBSCRIPTION.BCARDS_INCLUDED')}
                </div>
              </div>
              <div className='text-end'>
                <div className='csm-text-md csm-text-regular'>
                  {t('CANCEL_SUBSCRIPTION.NEXT_BILLING_DATE')}
                </div>
                <div className='fw-bold text-dark csm-text-lg csm-text-bold'>
                  {nextBillingDate || 'Oct 09, 2025'}
                </div>
              </div>
            </div>
          </div>

          <div className='rounded p-4 csm-alt-card'>
            <div className='text-dark mb-2 csm-text-md csm-text-semibold'>
              {t('CANCEL_SUBSCRIPTION.PREFER_NOT_CANCEL')}
            </div>
            <div className='d-flex align-items-center'>
              <span className='rounded-circle me-2 d-inline-block csm-bullet-dot'></span>
              <span className='csm-text-md csm-text-regular'>
                <span className='fw-semibold csm-text-semibold'>
                  {t('CANCEL_SUBSCRIPTION.DOWNGRADE')}
                </span>{' '}
                {t('CANCEL_SUBSCRIPTION.KEEP_DATA')}
              </span>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className='d-flex border-top px-4 py-3'>
        <div className='d-flex gap-3 ms-auto'>
          <Button
            variant='outline-secondary'
            onClick={handleClose}
            className='rounded-3 csm-btn-outline-gray csm-button csm-button-regular'
          >
            {t('CANCEL_SUBSCRIPTION.BACK')}
          </Button>
          <Button
            variant='danger'
            onClick={() => setCurrentStep(2)}
            className='rounded-3 csm-btn-danger csm-button csm-button-bold'
          >
            {t('CANCEL_SUBSCRIPTION.CONTINUE_CANCEL')}
          </Button>
        </div>
      </Modal.Footer>
    </>
  )

  const renderStep2 = () => {
    const reasonsData = cancelReasonsResponse?.data
    const reasons = reasonsData?.reasons || []
    const reasonOptions = reasons.map((item) => ({
      key: item._id,
      label: item.reason,
    }))

    const isLoadingWithNoData = isLoadingReasons && reasonOptions.length === 0

    const hasSelectedReasons =
      Object.values(feedbackData.reasons).some(Boolean) || feedbackData.otherReason.trim()

    return (
      <>
        <Modal.Header closeButton className='border-bottom'>
          <h2 className='fs-5 fw-bold text-dark'>{t('CANCEL_SUBSCRIPTION.HELP_IMPROVE')}</h2>
        </Modal.Header>
        <Modal.Body className='px-4 py-4'>
          <p className='mb-4 text-muted csm-text-md csm-text-regular'>
            {t('CANCEL_SUBSCRIPTION.FEEDBACK_HELP')}
          </p>

          <div className='mb-4'>
            {isLoadingWithNoData ? (
              <div className='text-center py-3'>
                <div className='spinner-border spinner-border-sm text-secondary me-2' role='status'>
                  <span className='visually-hidden'>{t('CANCEL_SUBSCRIPTION.LOADING')}</span>
                </div>
                <span className='text-muted csm-text-md csm-text-regular'>
                  {t('CANCEL_SUBSCRIPTION.LOADING_REASONS')}
                </span>
              </div>
            ) : (
              reasonOptions.map((option: {key: string; label: string}) => (
                <div key={option.key} className='custom-btn-wrapper'>
                  <label className='custom-checkbox'>
                    {option.label}
                    <input
                      type='checkbox'
                      checked={!!feedbackData.reasons[option.key]}
                      onChange={() => handleReasonToggle(option.key)}
                    />
                    <span className='checkmark'></span>
                  </label>
                </div>
              ))
            )}
          </div>

          <div>
            <label className='form-label fw-bold mb-2 csm-text-md'>
              {t('CANCEL_SUBSCRIPTION.OTHER')}
            </label>
            <Form.Control
              as='textarea'
              rows={3}
              placeholder={t('CANCEL_SUBSCRIPTION.TELL_US_MORE')}
              value={feedbackData.otherReason}
              onChange={(e) => setFeedbackData((prev) => ({...prev, otherReason: e.target.value}))}
              className='rounded-3 csm-textarea csm-text-md csm-text-regular'
            />
          </div>
        </Modal.Body>
        <Modal.Footer className='d-flex justify-content-between border-top px-4 py-3'>
          <Button
            variant='outline-secondary'
            onClick={() => setCurrentStep(1)}
            className='rounded-3 csm-btn-outline-gray csm-button csm-button-regular'
          >
            {t('CANCEL_SUBSCRIPTION.BACK')}
          </Button>
          <Button
            variant='danger'
            onClick={() => void handleFinalCancel()}
            disabled={isCancelling || !hasSelectedReasons}
            className='rounded-3 csm-btn-danger csm-button csm-button-bold'
          >
            {isCancelling ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                ></span>
                {t('CANCEL_SUBSCRIPTION.CANCELLING')}
              </>
            ) : (
              t('CANCEL_SUBSCRIPTION.SUBMIT_CONFIRM')
            )}
          </Button>
        </Modal.Footer>
      </>
    )
  }

  const renderStep3 = () => (
    <>
      <Modal.Header closeButton className='border-bottom'>
        <h2 className='fs-5 fw-bold text-dark'>{t('CANCEL_SUBSCRIPTION.SUBSCRIPTION_CANCELED')}</h2>
      </Modal.Header>
      <Modal.Body className='text-center px-4 py-5'>
        <div className='d-flex align-items-center justify-content-center rounded-circle mx-auto mb-4 csm-success-icon'>
          <i className='bi bi-check csm-icon-check'></i>
        </div>

        <h4 className='fw-bold mb-3 csm-text-xl'>
          {t('CANCEL_SUBSCRIPTION.SUBSCRIPTION_CANCELED')}
        </h4>
        <p className='text-muted mb-4 fw-bold csm-text-md csm-text-bold'>
          {t('CANCEL_SUBSCRIPTION.HAS_BEEN_CANCELED')}{' '}
          <span className='fw-bold text-dark'>{nextBillingDate}</span>.
        </p>

        {cancelError && (
          <div className='alert alert-danger d-flex align-items-center p-3 mt-4 text-start'>
            <i className='bi bi-exclamation-circle text-danger me-3 csm-icon-exclamation'></i>
            <div className='d-flex flex-column'>
              <span className='fw-bold mb-1 csm-text-bold'>{t('CANCEL_SUBSCRIPTION.ERROR')}</span>
              <span className='csm-text-regular'>{cancelError}</span>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className='border-top px-4 py-3'>
        <Button
          onClick={handleClose}
          className='w-100 rounded-3 csm-button csm-button-bold csm-button-purple'
        >
          {t('CANCEL_SUBSCRIPTION.RETURN_BILLING')}
        </Button>
      </Modal.Footer>
    </>
  )

  const renderCurrentStep = () => {
    if (currentStep === 1) return renderStep1()
    if (currentStep === 2) return renderStep2()
    if (currentStep === 3) return renderStep3()
    return null
  }

  return (
    <Modal
      show={show}
      onHide={() => !isCancelling && handleClose()}
      centered
      backdrop='static'
      keyboard={false}
      size='lg'
      className='cancel-subscription-modal'
    >
      <div className='modal-content csm-modal-content'>{renderCurrentStep()}</div>
    </Modal>
  )
}

export type {CancelFeedbackData}
export default CancelSubscriptionModal
